import React, { useState, useEffect, useCallback } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { getProjects, createProject, updateProject, deleteProject, getCountries } from '../utils/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';

const Projects = () => {
  usePageTitle('Projects');

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [countries, setCountries] = useState([]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    project_name: '',
    country: '',
    product: '',
    business_dev_name: '',
    product_owner_name: '',
  });
  const [saving, setSaving] = useState(false);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProjects({ search, page: pagination.page, limit: 20 });
      setProjects(response.data.projects);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Error loading projects');
    } finally {
      setLoading(false);
    }
  }, [search, pagination.page]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getCountries();
        setCountries(response.data);
      } catch (error) {
        console.error('Error loading countries:', error);
      }
    };
    loadCountries();
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const openModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        project_name: project.project_name || '',
        country: project.country || '',
        product: project.product || '',
        business_dev_name: project.business_dev_name || '',
        product_owner_name: project.product_owner_name || '',
      });
    } else {
      setEditingProject(null);
      setFormData({
        project_name: '',
        country: '',
        product: '',
        business_dev_name: '',
        product_owner_name: '',
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingProject(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_name) {
      toast.error('Project name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingProject) {
        await updateProject(editingProject.id, formData);
        toast.success('Project updated successfully');
      } else {
        await createProject(formData);
        toast.success('Project created successfully');
      }
      closeModal();
      loadProjects();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Are you sure you want to delete "${project.project_name}"?`)) {
      return;
    }

    try {
      await deleteProject(project.id);
      toast.success('Project deleted successfully');
      loadProjects();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting project');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">Manage your project database</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <PlusIcon style={{ width: 18, height: 18 }} />
          Add Project
        </button>
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <MagnifyingGlassIcon className="search-icon" style={{ width: 18, height: 18 }} />
            <input
              type="text"
              className="form-input search-input"
              placeholder="Search projects by ID, name, country, product, or people..."
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-overlay">
            <div className="spinner" />
            <span>Loading projects...</span>
          </div>
        ) : projects.length > 0 ? (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Project ID</th>
                    <th>Project Name</th>
                    <th>Country</th>
                    <th>Product</th>
                    <th>Business Dev</th>
                    <th>Product Owner</th>
                    <th style={{ width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <tr key={project.id}>
                      <td className="font-mono">{project.project_id}</td>
                      <td><strong>{project.project_name}</strong></td>
                      <td>{project.country || '-'}</td>
                      <td>{project.product || '-'}</td>
                      <td>{project.business_dev_name || '-'}</td>
                      <td>{project.product_owner_name || '-'}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => openModal(project)}
                            title="Edit"
                          >
                            <PencilIcon style={{ width: 16, height: 16 }} />
                          </button>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => handleDelete(project)}
                            title="Delete"
                            style={{ color: 'var(--color-accent-danger)' }}
                          >
                            <TrashIcon style={{ width: 16, height: 16 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  ‹
                </button>
                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                      onClick={() => setPagination((prev) => ({ ...prev, page: pageNum }))}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  className="pagination-btn"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  ›
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <BriefcaseIcon className="empty-state-icon" />
            <p className="empty-state-title">No projects found</p>
            <p>{search ? 'Try adjusting your search' : 'Add your first project to get started'}</p>
            {!search && (
              <button className="btn btn-primary" onClick={() => openModal()} style={{ marginTop: '1rem' }}>
                <PlusIcon style={{ width: 18, height: 18 }} />
                Add Project
              </button>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingProject ? 'Edit Project' : 'Add New Project'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editingProject ? 'Update Project' : 'Create Project'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Project Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.project_name}
              onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
              placeholder="Enter project name"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Country</label>
              <select
                className="form-select"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              >
                <option value="">Select country</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Product</label>
              <input
                type="text"
                className="form-input"
                value={formData.product}
                onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                placeholder="Enter product name"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Business Dev Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.business_dev_name}
                onChange={(e) => setFormData({ ...formData, business_dev_name: e.target.value })}
                placeholder="Enter business dev name"
              />
            </div>
            <div className="form-group mb-0">
              <label className="form-label">Product Owner Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.product_owner_name}
                onChange={(e) => setFormData({ ...formData, product_owner_name: e.target.value })}
                placeholder="Enter product owner name"
              />
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Projects;
